import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RemoveIcon from '@mui/icons-material/Remove';
import StatusDropdown from './StatusDropdown';
import { BRAND } from '../../theme/brandTheme';
import { canWriteReports } from '../../utils/roleUtils';

const STATUS_ACCENT = {
  Canlı: BRAND.green,
  Taslak: '#94A3B8',
  Durduruldu: '#F59E0B',
};

/** Format ISO date for list display */
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Primary result badge from analysis statuses */
const getResultMeta = (test) => {
  const statuses = test.resultStatuses || [];
  if (statuses.includes('Kazandı') && test.hasSignificant) {
    return { label: 'Kazandı', color: BRAND.green, icon: <EmojiEventsIcon sx={{ fontSize: 14 }} /> };
  }
  if (statuses.includes('Kaybetti')) {
    return { label: 'Kaybetti', color: '#EF4444', icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> };
  }
  if (statuses.includes('Etkisiz')) {
    return { label: 'Etkisiz', color: '#94A3B8', icon: <RemoveIcon sx={{ fontSize: 14 }} /> };
  }
  if (statuses.includes('Kazandı')) {
    return { label: 'Kazandı*', color: '#22C55E', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> };
  }
  return null;
};

/** Single report row — desktop grid + mobile stack */
const ReportListRow = ({
  test,
  brandName,
  user,
  activeDropdown,
  apiLoading,
  onToggleDropdown,
  onStatusChange,
  onReportDetail,
  onDeleteReport,
}) => {
  const result = getResultMeta(test);
  const accent = STATUS_ACCENT[test.status] || BRAND.purple;
  const dateLabel = test.formattedEndDate || test.formattedStartDate || formatDate(test.createdAt);
  const canDelete = user?.role === 'admin';
  const canEditStatus = canWriteReports(user) && (user?.role === 'admin' || user?.role === 'editor');

  return (
    <Box
      className="report-list-row"
      onClick={() => onReportDetail(test)}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(0, 1fr) 120px 130px 140px auto',
        },
        gap: { xs: 1.5, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 1.75 },
        borderRadius: 2,
        bgcolor: '#fff',
        border: `1px solid ${BRAND.borderLight}`,
        borderLeft: `4px solid ${result?.color || accent}`,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.15s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(37, 12, 75, 0.08)',
          borderColor: `${BRAND.purple}33`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Name + meta */}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: BRAND.purple,
            fontFamily: BRAND.fontFamily,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            lineHeight: 1.35,
          }}
        >
          {test.name}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
          {brandName && (
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
              {brandName}
            </Typography>
          )}
          {test.testDuration && (
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
              · {test.testDuration} gün
            </Typography>
          )}
          {typeof test.maxImprovement === 'number' && (
            <Typography
              variant="caption"
              sx={{
                color: test.maxImprovement >= 0 ? BRAND.green : '#EF4444',
                fontWeight: 600,
              }}
            >
              · {test.maxImprovement >= 0 ? '+' : ''}
              {test.maxImprovement.toFixed(1)}%
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Status — stop row click */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Typography
          variant="caption"
          sx={{ display: { md: 'none' }, color: BRAND.textMuted, mb: 0.5, width: '100%' }}
        >
          Durum
        </Typography>
        <StatusDropdown
          test={test}
          isOpen={activeDropdown === test.id}
          isLoading={apiLoading[test.id] === true}
          canChangeStatus={canEditStatus}
          onToggle={onToggleDropdown}
          onStatusChange={onStatusChange}
          variant="chip"
        />
      </Box>

      {/* Result */}
      <Box>
        <Typography
          variant="caption"
          sx={{ display: { md: 'none' }, color: BRAND.textMuted, mb: 0.5 }}
        >
          Sonuç
        </Typography>
        {result ? (
          <Chip
            size="small"
            icon={result.icon}
            label={result.label}
            sx={{
              height: 28,
              fontWeight: 600,
              bgcolor: `${result.color}14`,
              color: result.color,
              border: `1px solid ${result.color}40`,
              '& .MuiChip-icon': { color: result.color },
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: BRAND.textMuted, fontSize: 13 }}>
            —
          </Typography>
        )}
      </Box>

      {/* Date */}
      <Box>
        <Typography
          variant="caption"
          sx={{ display: { md: 'none' }, color: BRAND.textMuted, mb: 0.5 }}
        >
          Tarih
        </Typography>
        <Typography variant="body2" sx={{ color: BRAND.textGray, fontSize: 13, fontWeight: 500 }}>
          {dateLabel}
        </Typography>
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent={{ xs: 'flex-end', md: 'flex-end' }}
        onClick={(e) => e.stopPropagation()}
      >
        {canDelete && (
          <Tooltip title="Sil">
            <IconButton
              size="small"
              onClick={() => onDeleteReport(test.id, test.name, brandName)}
              sx={{
                color: '#EF4444',
                '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Raporu incele">
          <IconButton
            size="small"
            onClick={() => onReportDetail(test)}
            sx={{
              color: BRAND.purple,
              bgcolor: `${BRAND.purple}0A`,
              '&:hover': { bgcolor: `${BRAND.purple}18` },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default ReportListRow;
